import { persistStore } from 'src/persist-store';

const SWR_PREFIX = 'swr:';

/**
 * SWR cache provider backed by persist-store session storage.
 *
 * Hydrates from sessionStorage on init (synchronous read),
 * writes through to persist-store on every cache set (async, fire-and-forget).
 * clearOnLogout() removes all swr:* entries automatically (persistOnLogout: false).
 */
export function persistCacheProvider(): Map<string, any> {
  const map = new Map<string, any>();

  // Hydrate: read all swr:* keys from sessionStorage synchronously.
  // We bypass persist-store's async wrapper here because SWR needs sync init.
  for (let i = 0; i < sessionStorage.length; i++) {
    const rawKey = sessionStorage.key(i);
    if (rawKey?.startsWith(SWR_PREFIX)) {
      try {
        const swrKey = rawKey.slice(SWR_PREFIX.length);
        const value = JSON.parse(sessionStorage.getItem(rawKey)!);
        map.set(swrKey, value);
      } catch {
        // Corrupt entry — skip
      }
    }
  }

  const originalSet = map.set.bind(map);
  const originalDelete = map.delete.bind(map);

  map.set = (key: string, value: any) => {
    originalSet(key, value);

    // Write-through to persist-store (fire-and-forget).
    // Skip non-serializable entries (SWR stores functions for $req$ keys).
    if (value != null && typeof value !== 'function') {
      try {
        persistStore.putSession(SWR_PREFIX + key, value).catch(() => {});
      } catch {
        // Serialization failure — skip persistence for this entry
      }
    }

    return map;
  };

  map.delete = (key: string) => {
    const result = originalDelete(key);
    persistStore.removeSession(SWR_PREFIX + key).catch(() => {});
    return result;
  };

  return map;
}
